import { ColumnDef } from "@tanstack/table-core/build/lib/types";
import { Consignment } from "../../graphql/graphql";
import { getCurrentStep } from "../../shared/Utils";
import { Button, StatusIndicator } from "@nike/eds";
import { useNavigate } from "react-router-dom";
import { mapHealthStatusToIndicator } from "../../shared/Utils";
import { ContainerNumbersPopover } from "../../shared/ContainerNumbersPopover";

export type ConsignmentsTableColumn<T> = ColumnDef<T, any> & {
  header: string | React.ReactNode | (() => React.ReactNode);
  accessorKey: keyof T | string;
  filterLabel?: string;
  filterType?: "select" | "text" | "date";
  filterOptions?: Array<{ value: string; label: string }>;
  getFilterOptions?: (tableState: any) => Array<{ value: string; label: string }>;
  enableHiding?: boolean;
  enableSorting?: boolean;
};

const ViewConsignmentButton = ({ consignmentId }: { consignmentId: string }) => {
  const navigate = useNavigate();
  return (
    <Button
      variant="primary"
      size="small"
      onClick={() => navigate(`/consignment/${consignmentId}`)}
    >
      View
    </Button>
  );
};

export const getConsignmentsTableColumns = (): ConsignmentsTableColumn<Consignment>[] => {
    return [
  {
    header: () => (<span title="Health Status">Health</span>),
    accessorKey: "health",
    cell: ({ row }) => <StatusIndicator size='x-large' label={row.original.health?.health} status={mapHealthStatusToIndicator(row.original.health?.health)} />,
    size: 150,
    enableColumnFilter: false,
    enableSorting: false,
  },
  {
    header: () => (<span title="Consignment ID">Consignment ID</span>),
    accessorKey: "id",
    size: 150,
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    header: () => (<span title="BOL Number">BOL</span>),
    accessorKey: "billOfLadingNbr",
    size: 150,
    enableSorting: false,
    filterLabel: "BOL Number",
    enableColumnFilter: false,
  },
  {
    header: () => (<span title="Container Numbers">Container(s)</span>),
    accessorKey: "containerNbrs",
    cell: ({ row }) => {
      const containerNbrs = (row.original.containerNbrs || []).filter((n: string | null | undefined): n is string => !!n);
      return containerNbrs.length > 1
        ? (<><span className="ellipsis">{containerNbrs.join(", ")}</span><ContainerNumbersPopover containerNumbers={containerNbrs} /></>)
        : containerNbrs[0] || "N/A";
    },
    size: 150,
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    header: () => (<span title="Active Step">Active Step</span>),
    accessorKey: "steps",
    size: 150,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const currentStep = getCurrentStep(row.original.steps || []);
      return currentStep ? currentStep.title : "N/A";
    }
  },
  {
    header: () => (<span title="Open Exceptions">Open Exceptions</span>),
    accessorKey: "openExceptionCount",
    size: 150,
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    header: () => (<span title="Shipment Count (Actual/Expected)">Shipment Count</span>),
    accessorKey: "actualShipmentCount",
    size: 160,
    cell: ({ row }) => (<span style={{ color: row.original.actualShipmentCount ? 'inherit' : 'red' }}>{row.original.actualShipmentCount?row.original.actualShipmentCount:'--'} / {row.original.expectedShipmentCount?row.original.expectedShipmentCount:'--'}</span>),
    enableColumnFilter: false,
    enableSorting: false,
  },
  {
    header: () => (<span title="Actions">Actions</span>),
    accessorKey: "actions",
    size: 160,
    cell: ({ row }) => (
      <div className="actions-cell" >
        <ViewConsignmentButton consignmentId={row.original.id} />
      </div>
    ),
    enableColumnFilter: false,
    enableSorting: false,
  },
]};

